import React, { useEffect, useReducer, useRef, useState } from "react";

import { Input, InputChangeEvent, SwitchChangeEvent } from "@progress/kendo-react-inputs";
import { getInitialState } from "@selas/state-management";
import { EntityEditor, handleChange, IAddEntityScreenProps, IEditEntityScreenProps, YesNoSwitch } from "@selas/ui-components";
import { isNullOrEmpty } from "@selas/utils";
import { useTranslation } from "react-i18next";

import Endpoint from "../../../services/api/endpoint";
import { degreeReducer } from "../../../state/reducers";
import { IDegree } from "../../../utils/types/models";

const DegreeEditor: React.FC<IAddEntityScreenProps<IDegree> | IEditEntityScreenProps<IDegree>> = (props: IAddEntityScreenProps<IDegree> | IEditEntityScreenProps<IDegree>) => {
	const { t } = useTranslation();

	const [state, dispatch] = useReducer(degreeReducer, getInitialState<IDegree>());
	const initialDegree: IDegree = {
		id: (props as IEditEntityScreenProps<IDegree>).recordId || 0,
		name: "",
		active: true
	};
	const [degree, setDegree] = useState<IDegree>(initialDegree);
	const [dataChanged, setDataChanged] = useState<boolean>(false);
	const firstField: React.MutableRefObject<Input> = useRef();

	useEffect(() => {
		if (state.entity) {
			setDegree(state.entity);
		}
	}, [state.entity]);

	function onChange(event: InputChangeEvent | SwitchChangeEvent): void {
		setDegree(handleChange(degree, event));
		setDataChanged(true);
	}

	function getErrorMessages(): string[] {
		const messages: string[] = [];
		if (isNullOrEmpty(degree.name)) {
			messages.push(t("fill_in_required_field", { field: t("name").toLowerCase() }));
		}
		return messages;
	}

	function onActionButtonClicked(close: boolean, record: IDegree): void {
		if (record && !close) {
			setDegree(record);
			setDataChanged(false);
		}
		props.actionButtonClicked(close, record);
	}

	const readonly: boolean = (props as IEditEntityScreenProps<IDegree>).readonly;

	return (
		<EntityEditor
			width="70%"
			record={degree}
			endpoint={Endpoint.Degrees}
			entityState={state}
			entityType={t("degree")}
			dispatch={dispatch}
			dataChanged={dataChanged}
			readonly={readonly}
			recordName={degree.name}
			actionButtonClicked={onActionButtonClicked}
			getErrorMessages={getErrorMessages}
			firstFieldRef={firstField}
		>
			<div className="k-form">
				<label className="k-form-field">
					<span>{t("name")} *</span>
					<Input name="name" ref={firstField} value={degree.name} onChange={onChange} required disabled={readonly} />
				</label>
				{!props.hideActive && (
					<div className="k-form-field">
						<div>{t("active")}</div>
						<YesNoSwitch name="active" checked={degree.active} onChange={onChange} disabled={readonly} />
					</div>
				)}
			</div>
		</EntityEditor>
	);
};

export default DegreeEditor;
